import { logger } from '@pkg/logger';
import { LOCALE_CODE } from '@pkg/config/languages/localeCodes';

const labelsCache: Partial<Record<LOCALE_CODE, any>> = {};
const labelsPromises: Partial<Record<LOCALE_CODE, Promise<any>>> = {};

const formatLocaleForFilename = (locale: string): string => {
  return locale.toUpperCase().replace(/_/g, '-');
};

const getTranslationsFromArray = (data: any): any => {
  if (Array.isArray(data) && data.length > 0) {
    return data[0];
  }
  return data || {};
};

/**
 * Fetches and caches Sanity content labels for a given locale.
 * Returns cached data if available, otherwise fetches from static JSON.
 *
 * @param locale - The locale code (e.g., 'es', 'en-gb')
 * @returns Promise resolving to the labels object
 */
export const useBuildTimeSanityContent = async(locale: string): Promise<any> => {
  const currentLocale = (locale || LOCALE_CODE.EN_GB) as LOCALE_CODE;

  if (labelsCache[currentLocale]) {
    return getTranslationsFromArray(labelsCache[currentLocale]);
  }

  if (typeof window === 'undefined') {
    return {};
  }

  if (labelsPromises[currentLocale]) {
    return await labelsPromises[currentLocale];
  }

  // Fetch and cache
  const localeFilename = formatLocaleForFilename(currentLocale);
  const labelsUrl = `/sanity--content/labels-${localeFilename}.json`;

  labelsPromises[currentLocale] = (async() => {
    try {
      const response = await fetch(labelsUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${labelsUrl}: ${response.status}`);
      }
      const data = await response.json();
      labelsCache[currentLocale] = data;
      const translations = getTranslationsFromArray(data);
      logger.info(`Sanity content loaded for locale ${currentLocale}`);
      return translations;
    } catch (error) {
      logger.error(`Failed to load ${labelsUrl}:`, error);
      labelsCache[currentLocale] = {};
      return {};
    }
  })();

  return await labelsPromises[currentLocale];
};
